import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Check,
  Calendar,
  ClipboardList,
  Link2,
  UserRound,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import Navbar from "@/components/shared/Navbar";
import Footer from "@/components/shared/Footer";

const STEPS = [
  {
    icon: ClipboardList,
    title: "Complete your onboarding questionnaire",
    text: "Share your brand voice, offers, and goals so we can tune your growth engine.",
  },
  {
    icon: Calendar,
    title: "Schedule your onboarding session",
    text: "A 30-minute kickoff call with your Growth Specialist.",
  },
  {
    icon: Link2,
    title: "Connect your social accounts",
    text: "Give our team publishing access so your content goes live in your voice.",
  },
  {
    icon: UserRound,
    title: "Complete your business profile",
    text: "Details that help your Growth Advisor represent you accurately.",
  },
  {
    icon: Sparkles,
    title: "We start preparing your marketing strategy",
    text: "Your dedicated team drafts your first month\u2019s plan and lead-gen campaigns.",
  },
];

export default function ThankYouPage() {
  useEffect(() => {
    document.title = "Welcome aboard · WinCampaign DFY Growth Accelerator™";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="wc-thank-you-page" className="min-h-screen bg-white">
      <Navbar />

      <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-20 bg-[#30265b] text-white overflow-hidden wc-grain">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[#673dfc]/40 blur-3xl" />
        <div className="max-w-3xl mx-auto px-5 sm:px-8 relative text-center">
          <div className="inline-flex items-center gap-2 bg-[#d3fc3a] text-[#1a1a24] font-display font-semibold text-xs uppercase tracking-wider rounded-full px-3 py-1 mb-6">
            <Check size={13} strokeWidth={3} /> Enrollment confirmed
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Congratulations — welcome to the team.
          </h1>
          <p className="mt-5 text-lg text-white/80">
            You&apos;re officially enrolled in the WinCampaign DFY Business
            Growth Accelerator™. Here&apos;s exactly what happens next.
          </p>
        </div>
      </section>

      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="text-xs font-nav font-semibold uppercase tracking-wider text-[#673dfc] mb-3">
            Your next steps
          </div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#30265b] leading-tight">
            A short checklist to get your growth engine running.
          </h2>

          <ol className="mt-10 space-y-4">
            {STEPS.map((s, i) => (
              <li
                key={s.title}
                data-testid={`wc-thankyou-step-${i + 1}`}
                className="rounded-2xl border border-gray-200 bg-white p-6 flex gap-5 hover:border-[#c5b4ff] transition-colors"
              >
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-full bg-[#30265b] text-[#d3fc3a] grid place-items-center font-display font-bold">
                    {i + 1}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-[#673dfc]">
                    <s.icon size={16} />
                    <div className="text-xs font-nav font-semibold uppercase tracking-wider">
                      Step {i + 1}
                    </div>
                  </div>
                  <div className="mt-1 font-display font-bold text-lg text-[#30265b]">
                    {s.title}
                  </div>
                  <p className="mt-1 text-gray-600 text-sm sm:text-base leading-relaxed">
                    {s.text}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-12 rounded-3xl bg-[#f8f6ff] border border-[#e5dfff] p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div>
              <div className="font-display font-bold text-lg text-[#30265b]">
                Ready when you are.
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Once your onboarding is complete, your dedicated team gets to
                work.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href="#"
                data-testid="wc-thankyou-onboarding-cta"
                className="bg-[#673dfc] text-white font-display font-semibold rounded-full px-5 py-3 flex items-center gap-2 hover:bg-[#522ce0] transition-colors"
              >
                Start onboarding <ArrowRight size={15} />
              </a>
              <a
                href="#"
                data-testid="wc-thankyou-schedule-cta"
                className="border border-[#30265b] text-[#30265b] font-display font-semibold rounded-full px-5 py-3 flex items-center gap-2 hover:bg-[#30265b] hover:text-white transition-colors"
              >
                <Calendar size={15} /> Schedule kickoff
              </a>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/"
              className="text-sm font-nav font-medium text-gray-500 hover:text-[#30265b]"
            >
              ← Back to homepage
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
